import {useEffect, useState} from 'react';
import {
  MAX_SECTION_WIDTH,
  useDocViewDimensions,
} from './useDocViewDimensions.js';

const NAVBAR_WIDTH = 72;

export const useHorizontalResize = (
  windowWidth: number,
  isResizingRef: React.RefObject<boolean>,
  sideBeingResizedRef: React.RefObject<string | null>,
  leftLength: number,
  rightLength: number
) => {
  const [leftWidth, setLeftWidth] = useState<number>(25);
  const [rightWidth, setRightWidth] = useState<number>(25);
  const [centerWidth, setCenterWidth] = useState<number>(50);
  const [collapsedSections, setCollapsedSections] = useState<string[]>([]);

  const {
    minSectionWidth,
    minSectionPercentage,
    minDocAreaPercentage,
    collapsedSectionPercentage,
    docAreaWidth,
    minWidthToExpand,
  } = useDocViewDimensions(windowWidth);

  const removeCollpase = (side: string) => {
    setCollapsedSections((prev) => prev.filter((p) => p !== side));
  };
  const addCollapse = (side: string) => {
    setCollapsedSections((prev) => [...prev, side]);
  };

  const expandCollapseOnClick = (
    sides: string[],
    isExpandSections: boolean
  ) => {
    if (isExpandSections) {
      if (sides.length === 2) {
        setLeftWidth(minSectionPercentage);
        setRightWidth(minSectionPercentage);
        setCenterWidth(100 - 2 * minSectionPercentage);
        removeCollpase('left');
        removeCollpase('right');
      } else {
        if (sides.includes('left')) {
          setLeftWidth(minSectionPercentage);
          setCenterWidth(100 - rightWidth - minSectionPercentage);
          removeCollpase('left');
        }
        if (sides.includes('right')) {
          setRightWidth(minSectionPercentage);
          setCenterWidth(100 - leftWidth - minSectionPercentage);
          removeCollpase('right');
        }
      }
    } else {
      if (sides.length === 2) {
        setLeftWidth(collapsedSectionPercentage);
        setRightWidth(collapsedSectionPercentage);
        setCenterWidth(100 - 2 * collapsedSectionPercentage);
        addCollapse('left');
        addCollapse('right');
      } else {
        if (sides.includes('left')) {
          setLeftWidth(collapsedSectionPercentage);
          setCenterWidth(100 - rightWidth - collapsedSectionPercentage);
          addCollapse('left');
        }
        if (sides.includes('right')) {
          setRightWidth(collapsedSectionPercentage);
          setCenterWidth(100 - leftWidth - collapsedSectionPercentage);
          addCollapse('right');
        }
      }
    }
  };

  useEffect(() => {
    const stopResize = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      sideBeingResizedRef.current = null;
      document.body.style.cursor = '';
    };
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      window.dispatchEvent(new Event('resize'));
      const clientX = e.clientX - NAVBAR_WIDTH;
      const newPercentage = (clientX / docAreaWidth) * 100;
      if (sideBeingResizedRef.current === 'left-center') {
        const newCenterWidth = Math.max(
          100 - rightWidth - newPercentage,
          minDocAreaPercentage
        );
        const tempRight = 100 - newPercentage - newCenterWidth;
        const allowSizechange =
          clientX <= MAX_SECTION_WIDTH &&
          (tempRight > collapsedSectionPercentage ||
            newCenterWidth > minDocAreaPercentage);
        if (allowSizechange) {
          //If center is at minimum
          if (newCenterWidth === minDocAreaPercentage) {
            //check can we decrease right
            if (tempRight > minSectionPercentage) {
              // strart decreasing right and increasing left
              setLeftWidth(newPercentage);
              setRightWidth(tempRight);
            } else if (tempRight !== collapsedSectionPercentage) {
              // If below min width , then collapse right and give space to center
              setLeftWidth(newPercentage);
              setRightWidth(collapsedSectionPercentage);
              setCenterWidth(100 - newPercentage - collapsedSectionPercentage);
              if (rightLength > 0)
                setCollapsedSections((prev) => [...prev, 'right']);
            }
          } else {
            //prevent beyond self collapse
            if (newPercentage > collapsedSectionPercentage) {
              //increase left
              setLeftWidth(newPercentage);
              //decrease center
              setCenterWidth(newCenterWidth);
              if (newPercentage > minSectionPercentage) {
                setCollapsedSections((prev) =>
                  prev.filter((p) => p !== 'left')
                );
              }
            }
          }
        }
      } else if (sideBeingResizedRef.current === 'center-right') {
        const newRight = 100 - newPercentage;

        const newCenterWidth = Math.max(
          100 - leftWidth - newRight,
          minDocAreaPercentage
        );
        const tempLeft = 100 - newRight - newCenterWidth;
        const allowSizechange =
          windowWidth - e.clientX < MAX_SECTION_WIDTH &&
          (tempLeft > collapsedSectionPercentage ||
            newCenterWidth > minDocAreaPercentage);
        if (allowSizechange) {
          //If center is at minimum
          if (newCenterWidth === minDocAreaPercentage) {
            if (tempLeft > minSectionPercentage) {
              // decrease left and increase right
              setLeftWidth(tempLeft);
              setRightWidth(newRight);
            } else if (tempLeft !== collapsedSectionPercentage) {
              // If below min width , then collapse right and give space to center
              setRightWidth(newRight);
              setLeftWidth(collapsedSectionPercentage);
              setCenterWidth(100 - newRight - collapsedSectionPercentage);
              if (leftLength > 0)
                setCollapsedSections((prev) => [...prev, 'left']);
            }
          } else {
            // prevent self collpase when decreasing on right side
            if (newRight > collapsedSectionPercentage) {
              //Keep increasing right and decreasing center
              setRightWidth(newRight);
              setCenterWidth(newCenterWidth);
              if (newRight > minSectionPercentage) {
                setCollapsedSections((prev) =>
                  prev.filter((p) => p !== 'right')
                );
              }
            }
          }
        }
      }
    };
    const onUp = () => {
      if (sideBeingResizedRef.current === 'center-right') {
        if (rightWidth < minSectionPercentage) {
          setRightWidth(collapsedSectionPercentage);
          setCenterWidth(100 - collapsedSectionPercentage - leftWidth);
          setCollapsedSections((prev) => [...prev, 'right']);
        }
        if (
          collapsedSections.includes('right') &&
          rightWidth > minWidthToExpand &&
          rightWidth < minSectionPercentage
        ) {
          setRightWidth(minSectionPercentage);
          setCenterWidth(100 - minSectionPercentage - leftWidth);
          setCollapsedSections((prev) => prev.filter((p) => p !== 'right'));
        }
      }

      if (sideBeingResizedRef.current === 'left-center') {
        if (leftWidth < minSectionPercentage) {
          setLeftWidth(collapsedSectionPercentage);
          setCenterWidth(100 - collapsedSectionPercentage - rightWidth);
          setCollapsedSections((prev) => [...prev, 'left']);
        }
        if (
          collapsedSections.includes('left') &&
          leftWidth > minWidthToExpand &&
          leftWidth < minSectionPercentage
        ) {
          setLeftWidth(minSectionPercentage);
          setCenterWidth(100 - minSectionPercentage - rightWidth);
          setCollapsedSections((prev) => prev.filter((p) => p !== 'left'));
        }
      }
      stopResize();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    window.addEventListener('mouseleave', stopResize);
    window.addEventListener('blur', stopResize);
    window.addEventListener('visibilitychange', () => {
      if (document.hidden) stopResize();
    });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', stopResize);
      window.removeEventListener('blur', stopResize);
      window.removeEventListener('visibilitychange', stopResize);
    };
  }, [
    collapsedSectionPercentage,
    collapsedSections,
    docAreaWidth,
    isResizingRef,
    leftLength,
    leftWidth,
    minDocAreaPercentage,
    minSectionPercentage,
    minSectionWidth,
    minWidthToExpand,
    rightLength,
    rightWidth,
    sideBeingResizedRef,
    windowWidth,
  ]);

  return {
    leftWidth,
    setLeftWidth,
    rightWidth,
    setRightWidth,
    centerWidth,
    setCenterWidth,
    collapsedSections,
    setCollapsedSections,
    expandCollapseOnClick,
  };
};
